import type { LogoutOptions } from '@auth0/auth0-react'

// Shared key for the ForceLogout session-timeout bookkeeping.
export const LOGIN_TIMESTAMP_KEY = 'loginTimestamp'

// Log the user out the same way ForceLogout does: flag the logout as forced (so
// the Login page shows the "you were logged out" dialog), drop the session
// timestamp, and clear the Auth0 session. Used both for the inactivity timeout
// and when an access token can no longer be acquired (e.g. a missing/expired
// refresh token) — both cases need the user to sign in again.
export const forceLogout = (logout: (options?: LogoutOptions) => Promise<void>) => {
  localStorage.setItem('isForceLogout', 'true')
  localStorage.removeItem(LOGIN_TIMESTAMP_KEY)
  logout({ logoutParams: { returnTo: window.location.origin } })
}

// Single place that knows how to start re-authentication. GraphQLProvider holds
// the Auth0 `logout`, so it registers the handler here; it can then be fired from
// anywhere a token turns out to be unusable — the Apollo link chain or the REST
// `api()` helper — without each of those needing access to the Auth0 hooks.
let reauthHandler: (() => void) | null = null
let reauthTriggered = false

export const setReauthHandler = (handler: (() => void) | null) => {
  reauthHandler = handler
}

// Fire the re-login flow at most once per page load (concurrent requests all fail
// the same way). Returns true only on the call that actually triggered it, so the
// caller can log the cause exactly once.
export const triggerReauth = (): boolean => {
  if (reauthTriggered) {
    return false
  }
  reauthTriggered = true
  reauthHandler?.()
  return true
}

// Auth0 surfaces these when the cached session can no longer be silently
// renewed (e.g. a session that predates refresh-token support, or an expired /
// rotated refresh token). They are not transient — retrying never recovers
// them, so the user must re-authenticate.
export const isReauthenticationRequired = (e: unknown): boolean => {
  const code = (e as { error?: string })?.error
  const message = e instanceof Error ? e.message : String(e)
  return (
    code === 'missing_refresh_token' ||
    code === 'login_required' ||
    code === 'invalid_grant' ||
    code === 'consent_required' ||
    code === 'interaction_required' ||
    /missing refresh token|login required|invalid_grant|consent required|interaction required/i.test(
      message
    )
  )
}

// Hasura returns these in a GraphQL error's `extensions.code` when it rejects the
// JWT we sent — expired, bad signature, or invalid claims. The session is dead
// and the user must sign in again. Deliberately excludes `access-denied`, which
// is a legitimate permission boundary (an authenticated user hitting a resource
// they cannot see) and must NOT force a logout.
export const REAUTH_GRAPHQL_ERROR_CODES = ['invalid-jwt']

export const isReauthGraphQLError = (code: unknown): boolean =>
  typeof code === 'string' && REAUTH_GRAPHQL_ERROR_CODES.includes(code)
